import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// MUI per-component imports (never barrel imports)
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Slide from "@mui/material/Slide";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// MUI icons
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// MUI DataGrid
import { DataGrid } from "@mui/x-data-grid";

import moment from "moment";
import {
    fetchQuizTracking,
    exportQuizTracking,
    fetchAdminCollabReview,
} from "../vsecuriteSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";
import ExcelManager from "../../../app/managers/excelManager";

/** Brand orange used for primary actions throughout the app */
const BRAND_ORANGE = "#FF9015";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

// Resolves a tracking row's team display:
//   real team name → "— Individuel —" when individually targeted with no team
//   → "Sans équipe" for orphan collabs
const getTeamDisplay = (row, lang) =>
    row?.team_name
        || (row?.is_individual_target
            ? Localize("VSEC_INDIVIDUAL_TARGET", lang)
            : Localize("VSEC_NO_TEAM_LABEL", lang));

/** Slide transition for fullscreen mobile dialog */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * QuizTracking
 *
 * Admin view that shows completion/score tracking for a single quiz.
 * Offers two tabs:
 *   - "Par Collaborateur" — one row per collab with their best score and status
 *   - "Par Équipe"        — aggregated stats per team with a progress bar
 *
 * Props:
 *   quiz   — { id, title, passing_score, status }
 *   onBack — called when the user clicks the back button
 */
const QuizTracking = ({ quiz, onBack }) => {
    const dispatch = useDispatch();
    const mounted = useRef(false);
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileRedux = useSelector((state) => state.settings.isMobile);
    const mobile = isMobileRedux === 2 || isMobileScreen;

    /** Currently selected tab index (0 = Par Collaborateur, 1 = Par Équipe) */
    const [activeTab, setActiveTab] = useState(0);

    /** Redux tracking data */
    const tracking = useSelector((state) => state.vsecurite.quizzes.tracking);
    const loading = useSelector((state) => state.vsecurite.quizzes.loading);
    const lang = useSelector((state) => state.settings.lang);

    // ── On mount: fetch tracking data for this quiz ──────────────────────────
    useEffect(() => {
        if (!mounted.current && quiz?.id) {
            dispatch(fetchQuizTracking({ id_quiz: quiz.id }));
        }
        mounted.current = true;
    }, [quiz?.id]);

    // ── Summary stats computed from tracking array ───────────────────────────

    /**
     * Total number of assigned collaborators (unique rows in tracking).
     */
    const totalAssigned = tracking.length;

    /**
     * Number of collaborators who have passed the quiz.
     */
    const totalPassed = useMemo(
        () => tracking.filter((row) => row.has_passed).length,
        [tracking]
    );

    /**
     * Success rate as a percentage string (e.g. "72%").
     * Returns "0%" when no one is assigned.
     */
    const successRate = useMemo(() => {
        if (totalAssigned === 0) return "0%";
        return `${Math.round((totalPassed / totalAssigned) * 100)}%`;
    }, [totalAssigned, totalPassed]);

    // ── Per-team aggregation (Tab 2) ─────────────────────────────────────────

    /**
     * Builds an array of team summary objects from the flat tracking rows.
     * Each object: { team_name, members, passed, rate }
     */
    const teamStats = useMemo(() => {
        const map = {};
        tracking.forEach((row) => {
            const team = getTeamDisplay(row, lang);
            if (!map[team]) {
                map[team] = { team_name: team, members: 0, passed: 0 };
            }
            map[team].members += 1;
            if (row.has_passed) map[team].passed += 1;
        });
        return Object.values(map).map((t) => ({
            ...t,
            rate: t.members > 0 ? Math.round((t.passed / t.members) * 100) : 0,
        }));
    }, [tracking, lang]);

    // ── Search state ─────────────────────────────────────────────────────────

    const [collabSearch, setCollabSearch] = useState("");
    const [teamSearch, setTeamSearch] = useState("");

    /** True while an Excel export is being generated/downloaded */
    const [exportLoading, setExportLoading] = useState(false);

    // ── Review dialog state ───────────────────────────────────────────────────

    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);

    const handleOpenReview = async (rowData) => {
        setReviewOpen(true);
        setReviewData(null);
        setReviewLoading(true);
        try {
            const res = await dispatch(fetchAdminCollabReview({ id_quiz: quiz.id, id_collab: rowData.id_collab })).unwrap();
            if (res?.responseData) setReviewData(res.responseData);
        } catch {
            setReviewData(null);
        } finally {
            setReviewLoading(false);
        }
    };

    const filteredTracking = useMemo(() => {
        const q = collabSearch.trim().toLowerCase();
        if (!q) return tracking;
        return tracking.filter((row) =>
            (row.lastname  || "").toLowerCase().includes(q) ||
            (row.firstname || "").toLowerCase().includes(q) ||
            getTeamDisplay(row, lang).toLowerCase().includes(q)
        );
    }, [tracking, collabSearch, lang]);

    const filteredTeamStats = useMemo(() => {
        const q = teamSearch.trim().toLowerCase();
        if (!q) return teamStats;
        return teamStats.filter((row) =>
            (row.team_name || "").toLowerCase().includes(q)
        );
    }, [teamStats, teamSearch]);

    // ── Export handler ───────────────────────────────────────────────────────

    /**
     * Exports the quiz tracking to an Excel workbook with two sheets
     * ("Par Collaborateur" and "Par Équipe"). The server export endpoint is
     * still hit (for audit/parity), but the file is generated client-side from
     * the tracking data already in state so it always reflects both tabs.
     */
    const handleExport = async () => {
        if (!tracking?.length) {
            showErrorToast(Localize("VSEC_TOAST_NO_DATA_EXPORT", lang));
            return;
        }
        setExportLoading(true);
        try {
            await dispatch(exportQuizTracking({ id_quiz: quiz.id }));

            await ExcelManager.getInstance().generateAndDownloadVSecuriteQuizTrackingReport(
                tracking,
                teamStats,
                quiz,
                lang
            );
            showSuccessToast(Localize("VSEC_TOAST_EXPORT_QUIZ_SUCCESS", lang));
        } catch {
            showErrorToast(Localize("VSEC_TOAST_EXPORT_QUIZ_ERROR", lang));
        } finally {
            setExportLoading(false);
        }
    };

    // ── Column body templates ────────────────────────────────────────────────

    /**
     * Renders the Statut column cell.
     * Green chip = "Réussi", orange chip = "En cours".
     */
    const statutTemplate = (rowData) => {
        if (rowData.has_passed) {
            return (
                <Chip
                    label={Localize("VSEC_STATUS_PASSED_CHIP", lang)}
                    size="small"
                    icon={<CheckCircleRoundedIcon style={{ fontSize: 14 }} />}
                    sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600, fontSize: "0.75rem", "& .MuiChip-icon": { color: "#2e7d32" } }}
                />
            );
        }
        if (rowData.in_progress_count > 0) {
            return (
                <Chip
                    label={Localize("VSEC_STATUS_IN_PROGRESS_CHIP", lang)}
                    size="small"
                    icon={<HourglassTopRoundedIcon style={{ fontSize: 14 }} />}
                    sx={{ backgroundColor: "#fff3e0", color: BRAND_ORANGE, fontWeight: 600, fontSize: "0.75rem", "& .MuiChip-icon": { color: BRAND_ORANGE } }}
                />
            );
        }
        if (rowData.attempt_count > 0) {
            return (
                <Chip
                    label={Localize("VSEC_STATUS_FAILED_CHIP", lang)}
                    size="small"
                    icon={<CancelRoundedIcon style={{ fontSize: 14 }} />}
                    sx={{ backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600, fontSize: "0.75rem", "& .MuiChip-icon": { color: "#c62828" } }}
                />
            );
        }
        return (
            <Chip
                label={Localize("VSEC_STATUS_NOT_STARTED_CHIP", lang)}
                size="small"
                icon={<RemoveCircleOutlineRoundedIcon style={{ fontSize: 14 }} />}
                sx={{ backgroundColor: "#f5f5f5", color: "#757575", fontWeight: 600, fontSize: "0.75rem", "& .MuiChip-icon": { color: "#757575" } }}
            />
        );
    };

    /**
     * Renders the Meilleur Score column cell.
     */
    const scoreTemplate = (rowData) => {
        const score = rowData.best_score ?? 0;
        const passing = quiz?.min_score ?? quiz?.passing_score ?? 0;
        const color = score >= passing ? "#2e7d32" : score >= passing * 0.7 ? BRAND_ORANGE : "#c62828";
        return (
            <Typography
                component="span"
                sx={{ fontWeight: 700, color, fontSize: "0.85rem" }}
            >
                {score}%
            </Typography>
        );
    };

    /**
     * Renders the Dernière tentative column cell as a formatted date.
     */
    const dateTemplate = (rowData) => {
        if (!rowData.last_attempt_date) return <span style={{ color: "#9e9e9e" }}>—</span>;
        return <span>{moment(rowData.last_attempt_date).format("DD/MM/YYYY HH:mm")}</span>;
    };

    /**
     * Renders the Taux de réussite column with a MUI LinearProgress bar
     * in the Per-Team tab.
     */
    const teamRateTemplate = (rowData) => {
        const color = rowData.rate >= 80 ? "#2e7d32" : rowData.rate >= 50 ? BRAND_ORANGE : "#c62828";
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 140 }}>
                <Box sx={{ flex: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={rowData.rate}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#f0f0f0",
                            "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 4 },
                        }}
                    />
                </Box>
                <Typography sx={{ fontWeight: 700, color, fontSize: "0.8rem", minWidth: 36 }}>
                    {rowData.rate}%
                </Typography>
            </Box>
        );
    };

    // ── Quiz status chip ─────────────────────────────────────────────────────

    const renderStatusChip = () => {
        const isPublished = quiz?.status === "published";
        return (
            <Chip
                label={isPublished ? Localize("VSEC_STATUS_PUBLISHED_CHIP", lang) : Localize("VSEC_STATUS_DRAFT_CHIP", lang)}
                size="small"
                sx={{
                    backgroundColor: isPublished ? "#e8f5e9" : "#f5f5f5",
                    color: isPublished ? "#2e7d32" : "#757575",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    ml: mobile ? 0 : 1.5,
                }}
            />
        );
    };

    // ── Mobile card renderer for tracking rows ──────────────────────────────

    const renderMobileCollabCard = (row) => (
        <Box
            key={row.id_collab}
            sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid rgba(0,40,128,0.06)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                p: 2,
                mb: 1.5,
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: "#1a1a2e" }}>
                        {row.firstname} {row.lastname}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#757575" }}>
                        {row.team_name || (row.is_individual_target ? Localize("VSEC_INDIVIDUAL_TARGET", lang) : "—")}
                    </Typography>
                </Box>
                {(row.attempt_count > 0 || row.has_passed) && (
                    <IconButton
                        size="small"
                        onClick={() => handleOpenReview(row)}
                        sx={{ color: "#1976d2", minWidth: 44, minHeight: 44 }}
                    >
                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                {statutTemplate(row)}
                {scoreTemplate(row)}
                <Typography variant="caption" sx={{ color: "#888" }}>
                    {row.total_attempts ?? 0} tentative{(row.total_attempts ?? 0) !== 1 ? "s" : ""}
                </Typography>
            </Box>
            {row.last_attempt_date && (
                <Typography variant="caption" sx={{ color: "#9e9e9e", mt: 0.5, display: "block" }}>
                    {moment(row.last_attempt_date).format("DD/MM/YYYY HH:mm")}
                </Typography>
            )}
        </Box>
    );

    const renderMobileTeamCard = (row) => (
        <Box
            key={row.team_name}
            sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid rgba(0,40,128,0.06)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                p: 2,
                mb: 1.5,
            }}
        >
            <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: "#1a1a2e", mb: 1 }}>
                {row.team_name}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: "#555" }}>
                    {Localize("VSEC_COL_MEMBERS", lang)}: <strong>{row.members}</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: "#555" }}>
                    {Localize("VSEC_COL_PASSED", lang)}: <strong>{row.passed}</strong>
                </Typography>
            </Box>
            {teamRateTemplate(row)}
        </Box>
    );

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                background: mobile ? "#fff" : "rgba(255,255,255,0.95)",
                mt: 0,
            }}
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: mobile ? 1 : 1.5,
                    px: mobile ? 1.5 : 3,
                    py: mobile ? 1.5 : 2,
                    borderBottom: "1px solid rgba(0,40,128,0.06)",
                    background: mobile ? "#fff" : "rgba(255,255,255,0.8)",
                    backdropFilter: mobile ? "none" : "blur(12px)",
                    WebkitBackdropFilter: mobile ? "none" : "blur(12px)",
                    flexShrink: 0,
                    flexWrap: mobile ? "wrap" : "nowrap",
                }}
            >
                {/* Back button */}
                <IconButton
                    onClick={onBack}
                    size="small"
                    sx={{
                        color: BRAND_ORANGE,
                        minWidth: 44,
                        minHeight: 44,
                        "&:hover": { backgroundColor: "#fff3e0" },
                    }}
                    aria-label={Localize("VSEC_TOOLTIP_BACK_QUIZ", lang)}
                >
                    <ArrowBackRoundedIcon />
                </IconButton>

                {/* Quiz title and status chip */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
                    <Typography
                        variant="h6"
                        component="h1"
                        sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: mobile ? "0.95rem" : "1.1rem", lineHeight: 1.3 }}
                    >
                        {quiz?.title ?? "Quiz"}
                    </Typography>
                    {renderStatusChip()}
                </Box>

                {/* Passing score badge */}
                {!mobile && (
                    <Box
                        sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            background: "#fff3e0",
                            border: `1px solid ${BRAND_ORANGE}30`,
                            flexShrink: 0,
                        }}
                    >
                        <Typography sx={{ fontSize: "0.78rem", color: BRAND_ORANGE, fontWeight: 600 }}>
                            {Localize("VSEC_PASSING_SCORE_LABEL", lang)} {quiz?.min_score ?? quiz?.passing_score ?? 0}%
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ── Summary stats ───────────────────────────────────────────── */}
            <Box
                sx={{
                    display: "flex",
                    gap: mobile ? 1 : 2,
                    px: mobile ? 1.5 : 3,
                    py: mobile ? 1.5 : 2,
                    flexShrink: 0,
                    flexWrap: "wrap",
                    borderBottom: "1px solid rgba(0,40,128,0.05)",
                    background: "rgba(250,250,250,0.7)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                }}
            >
                <StatCard label={Localize("VSEC_QUIZ_STAT_TOTAL_ASSIGNED", lang)} value={totalAssigned} color="#1565c0" mobile={mobile} />
                <StatCard label={Localize("VSEC_QUIZ_STAT_TOTAL_PASSED", lang)} value={totalPassed} color="#2e7d32" mobile={mobile} />
                <StatCard label={Localize("VSEC_QUIZ_STAT_SUCCESS_RATE", lang)} value={successRate} color={BRAND_ORANGE} mobile={mobile} />

                {/* Mobile: show passing score as a stat card */}
                {mobile && (
                    <StatCard
                        label={Localize("VSEC_PASSING_SCORE_LABEL", lang)}
                        value={`${quiz?.min_score ?? quiz?.passing_score ?? 0}%`}
                        color={BRAND_ORANGE}
                        mobile={mobile}
                    />
                )}
            </Box>

            {/* ── Tabs ─────────────────────────────────────────────────────── */}
            <Box sx={{ px: mobile ? 0 : 3, flexShrink: 0, background: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", borderBottom: "1px solid rgba(0,40,128,0.06)" }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    variant={mobile ? "fullWidth" : "standard"}
                    sx={{
                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: mobile ? "0.82rem" : "0.875rem", minHeight: 48, transition: `color 250ms ${EASE}`, "&:hover": { color: "rgba(26,26,46,0.8)" } },
                        "& .Mui-selected": { color: `${BRAND_ORANGE} !important`, fontWeight: 700 },
                        "& .MuiTabs-indicator": { backgroundColor: BRAND_ORANGE, height: 3, borderRadius: "3px 3px 0 0", boxShadow: "0 0 8px rgba(255,144,21,0.3)" },
                    }}
                >
                    <Tab label={Localize("VSEC_TAB_BY_COLLAB", lang)} id="tab-0" aria-controls="tabpanel-0" />
                    <Tab label={Localize("VSEC_TAB_BY_TEAM", lang)} id="tab-1" aria-controls="tabpanel-1" />
                </Tabs>
            </Box>

            {/* ── Tab panels ───────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

                {/* Tab 0 — Par Collaborateur */}
                {activeTab === 0 && (
                    <Box
                        role="tabpanel"
                        id="tabpanel-0"
                        aria-labelledby="tab-0"
                        sx={{ flex: 1, overflow: mobile ? "auto" : "hidden", display: "flex", flexDirection: "column", p: mobile ? 1.5 : 2, gap: 1.5 }}
                    >
                        {/* Search + Export row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: mobile ? "wrap" : "nowrap" }}>
                            <TextField
                                size="small"
                                placeholder={Localize("VSEC_SEARCH_PLACEHOLDER", lang)}
                                value={collabSearch}
                                onChange={(e) => setCollabSearch(e.target.value)}
                                fullWidth={mobile}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRoundedIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    flex: mobile ? undefined : 1,
                                    maxWidth: mobile ? "100%" : 320,
                                    "& input": { fontSize: "16px" },
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                        fontSize: "0.88rem",
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: BRAND_ORANGE,
                                        },
                                    },
                                }}
                            />
                            <Box sx={{ ml: mobile ? 0 : "auto", width: mobile ? "100%" : "auto" }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={exportLoading
                                    ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    : <FileDownloadRoundedIcon />}
                                onClick={handleExport}
                                disabled={loading || exportLoading || tracking.length === 0}
                                fullWidth={mobile}
                                sx={{
                                    backgroundColor: BRAND_ORANGE,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: "12px",
                                    minHeight: 44,
                                    boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                                    transition: `all 250ms ${EASE}`,
                                    "&:hover": { backgroundColor: "#e07d0d", boxShadow: "0 6px 20px rgba(255,144,21,0.35)", transform: "translateY(-1px) scale(1.02)" },
                                    "&:active": { transform: "scale(0.98)" },
                                }}
                            >
                                <Langs str="VSEC_BTN_EXPORT" />
                            </Button>
                            </Box>
                        </Box>

                        {mobile ? (
                            /* ── Mobile: card list ── */
                            <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
                                {loading ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                        <CircularProgress sx={{ color: BRAND_ORANGE }} />
                                    </Box>
                                ) : filteredTracking.length === 0 ? (
                                    <Typography sx={{ textAlign: "center", py: 4, color: "#999" }}>
                                        {Localize("VSEC_EMPTY_TRACKING_QUIZ", lang)}
                                    </Typography>
                                ) : (
                                    filteredTracking.map((row) => renderMobileCollabCard(row))
                                )}
                            </Box>
                        ) : (
                            /* ── Desktop: DataGrid ── */
                            <Box
                                sx={{
                                    height: 'calc(100vh - 360px)',
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,40,128,0.06)",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)",
                                }}
                            >
                                <DataGrid
                                    rows={filteredTracking}
                                    getRowId={(row) => `${row.id_collab}`}
                                    loading={loading}
                                    columns={[
                                        {
                                            field: "lastname",
                                            headerName: Localize("VSEC_COL_LASTNAME", lang),
                                            minWidth: 150,
                                            flex: 1,
                                        },
                                        {
                                            field: "firstname",
                                            headerName: Localize("VSEC_COL_FIRSTNAME", lang),
                                            minWidth: 150,
                                            flex: 1,
                                        },
                                        {
                                            field: "team_name",
                                            headerName: Localize("VSEC_COL_TEAM", lang),
                                            minWidth: 150,
                                            flex: 1,
                                            valueGetter: (_, row) => getTeamDisplay(row, lang),
                                        },
                                        {
                                            field: "total_attempts",
                                            headerName: Localize("VSEC_COL_NB_ATTEMPTS", lang),
                                            minWidth: 120,
                                            align: "center",
                                            headerAlign: "center",
                                        },
                                        {
                                            field: "best_score",
                                            headerName: Localize("VSEC_COL_BEST_SCORE", lang),
                                            minWidth: 140,
                                            renderCell: (params) => scoreTemplate(params.row),
                                        },
                                        {
                                            field: "has_passed",
                                            headerName: Localize("VSEC_COL_STATUS", lang),
                                            minWidth: 140,
                                            renderCell: (params) => statutTemplate(params.row),
                                        },
                                        {
                                            field: "last_attempt_date",
                                            headerName: Localize("VSEC_COL_LAST_ATTEMPT", lang),
                                            minWidth: 170,
                                            renderCell: (params) => dateTemplate(params.row),
                                        },
                                        {
                                            field: "_actions",
                                            headerName: "",
                                            width: 60,
                                            sortable: false,
                                            disableColumnMenu: true,
                                            align: "center",
                                            renderCell: (params) =>
                                                (params.row.attempt_count > 0 || params.row.has_passed) ? (
                                                    <Tooltip title={Localize("VSEC_TOOLTIP_VIEW_ANSWERS", lang)}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenReview(params.row)}
                                                            sx={{ color: "#1976d2", "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" } }}
                                                        >
                                                            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null,
                                        },
                                    ]}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 25 } },
                                    }}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    disableRowSelectionOnClick
                                    localeText={{
                                        noRowsLabel: Localize("VSEC_EMPTY_TRACKING_QUIZ", lang),
                                    }}
                                    sx={{
                                        borderRadius: "14px",
                                        border: "1px solid rgba(0,40,128,0.06)",
                                        "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(247,248,252,0.9)" },
                                        "& .MuiDataGrid-row": { transition: `background-color 200ms ${EASE}` },
                                        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "rgba(250,250,252,0.7)" },
                                        "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(255,144,21,0.05)" },
                                        "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,40,128,0.04)" },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}

                {/* Tab 1 — Par Équipe */}
                {activeTab === 1 && (
                    <Box
                        role="tabpanel"
                        id="tabpanel-1"
                        aria-labelledby="tab-1"
                        sx={{ flex: 1, overflow: mobile ? "auto" : "hidden", display: "flex", flexDirection: "column", p: mobile ? 1.5 : 2, gap: 1.5 }}
                    >
                        {/* Search bar */}
                        <TextField
                            size="small"
                            placeholder={Localize("VSEC_SEARCH_PLACEHOLDER", lang)}
                            value={teamSearch}
                            onChange={(e) => setTeamSearch(e.target.value)}
                            fullWidth={mobile}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: mobile ? "100%" : 320,
                                "& input": { fontSize: "16px" },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    fontSize: "0.88rem",
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: BRAND_ORANGE,
                                    },
                                },
                            }}
                        />

                        {mobile ? (
                            /* ── Mobile: card list ── */
                            <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
                                {loading ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                        <CircularProgress sx={{ color: BRAND_ORANGE }} />
                                    </Box>
                                ) : filteredTeamStats.length === 0 ? (
                                    <Typography sx={{ textAlign: "center", py: 4, color: "#999" }}>
                                        {Localize("VSEC_EMPTY_TEAM_DATA_QUIZ", lang)}
                                    </Typography>
                                ) : (
                                    filteredTeamStats.map((row) => renderMobileTeamCard(row))
                                )}
                            </Box>
                        ) : (
                            /* ── Desktop: DataGrid ── */
                            <Box
                                sx={{
                                    height: 'calc(100vh - 360px)',
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,40,128,0.06)",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)",
                                }}
                            >
                                <DataGrid
                                    rows={filteredTeamStats}
                                    getRowId={(row) => row.team_name}
                                    loading={loading}
                                    columns={[
                                        {
                                            field: "team_name",
                                            headerName: Localize("VSEC_COL_TEAM", lang),
                                            minWidth: 190,
                                            flex: 1,
                                        },
                                        {
                                            field: "members",
                                            headerName: Localize("VSEC_COL_MEMBERS", lang),
                                            minWidth: 110,
                                            align: "center",
                                            headerAlign: "center",
                                        },
                                        {
                                            field: "passed",
                                            headerName: Localize("VSEC_COL_PASSED", lang),
                                            minWidth: 110,
                                            align: "center",
                                            headerAlign: "center",
                                        },
                                        {
                                            field: "rate",
                                            headerName: Localize("VSEC_QUIZ_STAT_SUCCESS_RATE", lang),
                                            minWidth: 200,
                                            flex: 1,
                                            renderCell: (params) => teamRateTemplate(params.row),
                                        },
                                    ]}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 25 } },
                                    }}
                                    pageSizeOptions={[10, 25, 50]}
                                    disableRowSelectionOnClick
                                    localeText={{
                                        noRowsLabel: Localize("VSEC_EMPTY_TEAM_DATA_QUIZ", lang),
                                    }}
                                    sx={{
                                        borderRadius: "14px",
                                        border: "1px solid rgba(0,40,128,0.06)",
                                        "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(247,248,252,0.9)" },
                                        "& .MuiDataGrid-row": { transition: `background-color 200ms ${EASE}` },
                                        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "rgba(250,250,252,0.7)" },
                                        "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(255,144,21,0.05)" },
                                        "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,40,128,0.04)" },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>

        {/* ── Collab review dialog ──────────────────────────────────────── */}
        <Dialog
            open={reviewOpen}
            onClose={() => setReviewOpen(false)}
            maxWidth={mobile ? undefined : "md"}
            fullWidth={!mobile}
            fullScreen={mobile}
            TransitionComponent={mobile ? SlideUp : undefined}
            sx={{ zIndex: 1400 }}
            PaperProps={{ sx: {
                borderRadius: mobile ? 0 : "20px",
                maxHeight: mobile ? "100%" : "85vh",
                boxShadow: mobile ? "none" : "0 16px 48px rgba(0,40,128,0.12), 0 4px 16px rgba(255,144,21,0.06)",
                background: "#fff",
            } }}
        >
            {mobile ? (
                /* Mobile: sticky AppBar header */
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{ background: "#fff", borderBottom: "1px solid rgba(0,0,0,0.08)" }}
                >
                    <Toolbar sx={{ justifyContent: "space-between", minHeight: 56, px: 2 }}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>
                                {Localize("VSEC_REVIEW_DIALOG_TITLE", lang)}
                            </Typography>
                            {reviewData?.collab && (
                                <Typography sx={{ fontSize: "0.78rem", color: BRAND_ORANGE, fontWeight: 600 }}>
                                    {reviewData.collab.firstname} {reviewData.collab.lastname}
                                </Typography>
                            )}
                        </Box>
                        <IconButton onClick={() => setReviewOpen(false)} sx={{ minWidth: 44, minHeight: 44 }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
            ) : (
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                            {Localize("VSEC_REVIEW_DIALOG_TITLE", lang)}
                            {reviewData?.collab && (
                                <Box component="span" sx={{ color: BRAND_ORANGE, ml: 1 }}>
                                    — {reviewData.collab.firstname} {reviewData.collab.lastname}
                                </Box>
                            )}
                        </Typography>
                        {reviewData?.attempt && (
                            <Typography sx={{ fontSize: "0.8rem", color: "#757575", mt: 0.25 }}>
                                {Localize("VSEC_REVIEW_SCORE_LABEL", lang)}: {reviewData.attempt.score}%
                                {" · "}
                                {reviewData.attempt.passed
                                    ? <Box component="span" sx={{ color: "#2e7d32", fontWeight: 600 }}>{Localize("VSEC_STATUS_PASSED_CHIP", lang)}</Box>
                                    : <Box component="span" sx={{ color: "#c62828", fontWeight: 600 }}>{Localize("VSEC_STATUS_FAILED_CHIP", lang)}</Box>
                                }
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small" onClick={() => setReviewOpen(false)}>
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
            )}
            <Divider />
            <DialogContent sx={{ p: mobile ? 2 : 2.5 }}>
                {/* Score banner on mobile */}
                {mobile && reviewData?.attempt && (
                    <Box sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5,
                        mb: 2, px: 2, py: 1.25, borderRadius: "10px",
                        background: reviewData.attempt.passed ? "rgba(22,163,74,0.07)" : "rgba(220,38,38,0.07)",
                        border: `1px solid ${reviewData.attempt.passed ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.2)"}`,
                    }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: reviewData.attempt.passed ? "#2e7d32" : "#c62828" }}>
                            {reviewData.attempt.score}%
                        </Typography>
                        <Chip
                            label={reviewData.attempt.passed ? Localize("VSEC_STATUS_PASSED_CHIP", lang) : Localize("VSEC_STATUS_FAILED_CHIP", lang)}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                backgroundColor: reviewData.attempt.passed ? "#e8f5e9" : "#ffebee",
                                color: reviewData.attempt.passed ? "#2e7d32" : "#c62828",
                            }}
                        />
                    </Box>
                )}

                {reviewLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress sx={{ color: BRAND_ORANGE }} />
                    </Box>
                ) : !reviewData?.questions?.length ? (
                    <Typography sx={{ color: "#757575", textAlign: "center", py: 4 }}>
                        {Localize("VSEC_REVIEW_NO_ATTEMPTS", lang)}
                    </Typography>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {reviewData.questions.map((q, idx) => (
                            <ReviewQuestionCard key={q.id} index={idx + 1} question={q} lang={lang} mobile={mobile} />
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
        </>
    );
};

// ── Internal sub-component ───────────────────────────────────────────────────

/**
 * StatCard — small summary metric card shown in the stats strip.
 */
const StatCard = ({ label, value, color, mobile }) => (
    <Box
        sx={{
            px: mobile ? 1.5 : 2.5,
            py: mobile ? 1 : 1.5,
            borderRadius: mobile ? "10px" : "14px",
            background: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(0,40,128,0.06)",
            boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)",
            minWidth: mobile ? 0 : 120,
            flex: mobile ? 1 : undefined,
            textAlign: "center",
            transition: `all 300ms ${EASE}`,
            "&:hover": mobile ? undefined : {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 24px rgba(0,40,128,0.07), 0 2px 8px rgba(255,144,21,0.06)",
            },
        }}
    >
        <Typography sx={{ fontSize: mobile ? "1.15rem" : "1.5rem", fontWeight: 800, color, lineHeight: 1.2, letterSpacing: "-0.03em" }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "#757575", mt: 0.25, fontWeight: 500 }}>
            {label}
        </Typography>
    </Box>
);

function ReviewQuestionCard({ index, question, lang, mobile }) {
    const isCorrect = question.answer_is_correct;
    return (
        <Box sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden" }}>
            {/* Header */}
            <Box sx={{
                display: "flex", alignItems: "center", gap: mobile ? 1 : 1.5, px: mobile ? 1.5 : 2, py: 1.25,
                background: isCorrect ? "rgba(22,163,74,0.07)" : "rgba(220,38,38,0.07)",
                borderBottom: `1px solid ${isCorrect ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.2)"}`,
            }}>
                {isCorrect
                    ? <CheckCircleRoundedIcon sx={{ color: "#2e7d32", fontSize: 17, flexShrink: 0 }} />
                    : <CancelRoundedIcon sx={{ color: "#c62828", fontSize: 17, flexShrink: 0 }} />
                }
                <Typography sx={{ fontSize: mobile ? "0.84rem" : "0.88rem", fontWeight: 650, color: "#333", flex: 1, wordBreak: "break-word" }}>
                    <Box component="span" sx={{ color: "#9e9e9e", mr: 0.75 }}>Q{index}.</Box>
                    {question.question_text}
                </Typography>
            </Box>

            {/* Options */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, px: mobile ? 1.5 : 2, py: 1.5 }}>
                {question.options.map((opt) => {
                    const correct = opt.is_correct;
                    const selected = opt.was_selected;
                    let bg, border, color, Icon;
                    if (correct && selected)       { bg = "rgba(22,163,74,0.07)";  border = "rgba(22,163,74,0.25)";  color = "#2e7d32"; Icon = CheckCircleRoundedIcon; }
                    else if (correct && !selected) { bg = "rgba(22,163,74,0.07)";  border = "rgba(22,163,74,0.25)";  color = "#2e7d32"; Icon = CheckCircleRoundedIcon; }
                    else if (!correct && selected) { bg = "rgba(220,38,38,0.07)";  border = "rgba(220,38,38,0.25)";  color = "#c62828"; Icon = CancelRoundedIcon; }
                    else                           { bg = "transparent";            border = "rgba(0,0,0,0.08)";       color = "#9e9e9e"; Icon = RadioButtonUncheckedIcon; }
                    return (
                        <Box key={opt.id} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.8, borderRadius: "8px", background: bg, border: `1px solid ${border}`, minHeight: 40 }}>
                            <Icon sx={{ color, fontSize: 16, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "0.84rem", color: correct ? color : (selected ? color : "#9e9e9e"), fontWeight: correct ? 600 : 400, wordBreak: "break-word" }}>
                                {opt.option_text}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Explanation */}
            {question.explanation && (
                <Box sx={{ mx: mobile ? 1.5 : 2, mb: 1.5, px: 1.5, py: 1.25, borderRadius: "8px", background: "rgba(255,144,21,0.07)", border: "1px solid rgba(255,144,21,0.2)", display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <LightbulbOutlinedIcon sx={{ color: BRAND_ORANGE, fontSize: 16, mt: 0.2, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.82rem", color: "#555", lineHeight: 1.6 }}>
                        {question.explanation}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default QuizTracking;
